import type { LocalDate } from '@cadencia/contracts';
import { clamp, roundTo } from '../units/round.js';

/**
 * Aprendizaje del sesgo de los overrides
 * (docs/LOAD_RECOMMENDATION_ENGINE.md §7.2, requisito §11.4).
 *
 * QUE ESTABA PASANDO HASTA HOY
 *
 * `overrideBiasPct` se aplicaba en `recommend.ts`, emitia el motivo
 * `override_bias`, se guardaba en `progression_state` y se sincronizaba. Lo que
 * no existia era **quien lo calcula**: todos los caminos de escritura ponian
 * `existing?.overrideBiasPct ?? 0`, asi que el multiplicador valia 1 desde la
 * Fase 3 y habria valido 1 siempre. Un motor con la entrada cegada no falla:
 * decide de mas, plausiblemente, y por eso nadie lo mira. Es la misma forma que
 * las señales en `null` del §2.3 y que el `weekIntent` cableado.
 *
 * QUE APRENDE, Y QUE NO
 *
 * Aprende que el motor va corto —o largo— EN ESE EJERCICIO Y ESA MAQUINA, y lo
 * corrige como mucho un 5 %. No sustituye al motor ni aprende de una sola
 * observacion: hacen falta 5 overrides en 3 sesiones distintas dentro de 45
 * dias, y ademas tienen que haber FUNCIONADO.
 *
 * «Funcionado» es la parte que importa y la que no es obvia: si el usuario sube
 * la carga y falla el RIR objetivo, lo que se aprende **no** es que haya que
 * subir, sino que ese override no funciono. Por eso el fracaso no se ignora
 * —ignorarlo dejaria el sesgo anterior intacto— sino que empuja hacia 0, que es
 * el numero del motor. Aprender siempre en la direccion de lo que el usuario
 * teclea convertiria el ajuste en un acelerador.
 *
 * POR QUE NO CONOCE EL RELOJ
 *
 * Recibe `today` y dias locales ya resueltos (ADR-007). La ventana de 45 dias y
 * el decaimiento de 60 se cuentan en DIAS DE CALENDARIO LOCAL, no en instantes:
 * contarlos sobre `performedAt` en UTC movería el borde de la ventana en las
 * dos horas de la madrugada, que es la trampa que este repositorio ya conoce.
 */

export const OVERRIDE_BIAS = {
  /** Ventana de observaciones que se miran, en dias. */
  windowDays: 45,
  /** Cuantas de las mas recientes entran en la mediana. */
  maxObservations: 10,
  /** Minimo de overrides para aprender algo. */
  minObservations: 5,
  /** Minimo de sesiones DISTINTAS: cinco series de un mismo dia no son historia. */
  minSessions: 3,
  /** Margen de RIR dentro del cual el override se considera que funciono. */
  rirTolerance: 1,
  /** Proporcion de overrides que tienen que haber funcionado. */
  minSuccessRatio: 0.7,
  /** Suavizado exponencial: cuanto pesa lo nuevo frente a lo ya aprendido. */
  ewmaAlpha: 0.25,
  /** Tope duro del sesgo, en tanto por uno. Coincide con el contrato. */
  maxAbsBias: 0.05,
  /** Sin overrides nuevos en este plazo, el sesgo se olvida. */
  decayAfterDays: 60,
} as const;

export interface OverrideObservation {
  /** Dia local en que se hizo la serie (`YYYY-MM-DD`). */
  localDate: LocalDate;
  /**
   * Que sesion fue. Dos series del mismo dia y del mismo parte cuentan como
   * UNA sesion para el minimo de tres: el §7.2 pide tres ocasiones, no tres
   * series. Vale cualquier identificador estable; en la aplicacion es el parte.
   */
  sessionKey: string;
  /** Lo que el motor recomendo. Una observacion sin esto no es un override. */
  recommendedLoadKg: number;
  /** Lo que el usuario puso. */
  loadKg: number;
  /** RIR conseguido de verdad. */
  rir: number;
  /** RIR con el que estaba prescrita esa serie. */
  targetRir: number;
}

export interface OverrideBiasResult {
  /** El sesgo que debe quedar guardado, acotado a +/-5 %. */
  biasPct: number;
  /** Cuantas observaciones han entrado en la cuenta. */
  used: number;
  /** Proporcion de esas que quedaron dentro del RIR objetivo. */
  successRatio: number;
  reasonCodes: string[];
}

function mediana(valores: readonly number[]): number {
  const orden = [...valores].sort((a, b) => a - b);
  const medio = Math.floor(orden.length / 2);
  if (orden.length % 2 === 1) return orden[medio] ?? 0;
  return ((orden[medio - 1] ?? 0) + (orden[medio] ?? 0)) / 2;
}

/** Dias de calendario entre dos fechas locales. Sin husos: son dos `YYYY-MM-DD`. */
function diasEntre(desde: LocalDate, hasta: LocalDate): number {
  const a = Date.parse(`${desde}T00:00:00Z`);
  const b = Date.parse(`${hasta}T00:00:00Z`);
  return Math.round((b - a) / 86_400_000);
}

export function learnOverrideBias(input: {
  /**
   * TODOS los overrides conocidos de ese (ejercicio, equipamiento), en
   * cualquier orden. Se pasan sin filtrar por fecha a proposito: para saber si
   * toca olvidar hace falta ver los que quedan FUERA de la ventana de 45 dias.
   */
  observations: readonly OverrideObservation[];
  today: LocalDate;
  /** El sesgo vigente. Es la base del suavizado. */
  previousBiasPct: number;
}): OverrideBiasResult {
  const previo = clamp(input.previousBiasPct, -OVERRIDE_BIAS.maxAbsBias, OVERRIDE_BIAS.maxAbsBias);

  // Una recomendacion de 0 kg no puede producir un porcentaje, y una carga
  // igual a la recomendada no es un override por mucho que venga marcada.
  const validas = input.observations.filter(
    (obs) => obs.recommendedLoadKg > 0 && obs.loadKg !== obs.recommendedLoadKg,
  );

  if (validas.length === 0) {
    return { biasPct: 0, used: 0, successRatio: 0, reasonCodes: ['override_bias_sin_datos'] };
  }

  const ultimo = validas.reduce(
    (mayor, obs) => (obs.localDate > mayor ? obs.localDate : mayor),
    validas[0]!.localDate,
  );
  if (diasEntre(ultimo, input.today) > OVERRIDE_BIAS.decayAfterDays) {
    // Se olvida ENTERO y no por tramos: el §7.2 dice «decae hacia 0» y un
    // decaimiento gradual necesitaria guardar cuando se aprendio cada parte.
    return {
      biasPct: 0,
      used: 0,
      successRatio: 0,
      reasonCodes: previo === 0 ? ['override_bias_sin_datos'] : ['override_bias_olvidado'],
    };
  }

  const enVentana = validas
    .filter((obs) => diasEntre(obs.localDate, input.today) <= OVERRIDE_BIAS.windowDays)
    .sort((a, b) => a.localDate.localeCompare(b.localDate) || a.sessionKey.localeCompare(b.sessionKey))
    .slice(-OVERRIDE_BIAS.maxObservations);

  const sesiones = new Set(enVentana.map((obs) => obs.sessionKey)).size;
  if (enVentana.length < OVERRIDE_BIAS.minObservations || sesiones < OVERRIDE_BIAS.minSessions) {
    // No se aprende, y tampoco se desaprende: lo que ya habia sigue valiendo
    // hasta que caduque por el decaimiento de arriba.
    return {
      biasPct: previo,
      used: enVentana.length,
      successRatio: 0,
      reasonCodes: ['override_bias_insuficiente'],
    };
  }

  const acertadas = enVentana.filter(
    (obs) => Math.abs(obs.rir - obs.targetRir) <= OVERRIDE_BIAS.rirTolerance,
  ).length;
  const successRatio = acertadas / enVentana.length;
  const funciono = successRatio >= OVERRIDE_BIAS.minSuccessRatio;

  /*
   * EL SUAVIZADO SE DERIVA DEL HISTORIAL, NO SE ACUMULA SOBRE LO GUARDADO.
   *
   * El §7.2 escribe `EWMA(sesgo_bruto, alfa)` dando por hecho que eso se evalua
   * UNA VEZ por sesion. No es asi: `refreshProgressionState` se llama tambien
   * cada vez que se abre el panel de propuestas, y con la media exponencial
   * arrancando en el valor guardado, abrirlo cinco veces movia el sesgo cinco
   * escalones sin haber pisado el gimnasio —de 0,010 a 0,018 en dos visitas,
   * medido—. Es exactamente lo que le pasaba a `stallCounter` y por lo que
   * existe `stallCounterFromHistory`.
   *
   * Asi que la media se recorre sobre las SESIONES de la ventana, en orden y
   * empezando en 0: cada sesion aporta la mediana de sus overrides. El
   * resultado es una funcion pura del historial —dos llamadas seguidas dan el
   * mismo numero— y sigue siendo la media exponencial que pide el documento.
   */
  const porSesion = new Map<string, number[]>();
  for (const obs of enVentana) {
    const ratio = (obs.loadKg - obs.recommendedLoadKg) / obs.recommendedLoadKg;
    porSesion.set(obs.sessionKey, [...(porSesion.get(obs.sessionKey) ?? []), ratio]);
  }

  let suavizado = 0;
  if (funciono) {
    for (const ratios of porSesion.values()) {
      suavizado += OVERRIDE_BIAS.ewmaAlpha * (mediana(ratios) - suavizado);
    }
  }

  const biasPct = clamp(
    // Cuatro decimales: el contrato acepta cualquier numero en +/-0,05 y sin
    // redondear, el ruido de coma flotante genera una fila nueva —y una
    // operacion de sincronizacion— en cada recalculo.
    roundTo(suavizado, 4),
    -OVERRIDE_BIAS.maxAbsBias,
    OVERRIDE_BIAS.maxAbsBias,
  );

  return {
    biasPct,
    used: enVentana.length,
    successRatio,
    reasonCodes: funciono ? ['override_bias_aprendido'] : ['override_bias_no_funciono'],
  };
}
